import { useEffect, useState } from "react";
import { Loader2, Upload, CheckCircle2, Link as LinkIcon, User, Users, Trophy, Music4, FileCheck, StickyNote } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export const RITMICA_CATEGORIES = [
  "GIOVANISSIME (dal 2016 in poi)", "ALLIEVE 1 (2017-2018)", "ALLIEVE 2 (2016)", "ALLIEVE 3 (2015)",
  "ALLIEVE 4 (2014)", "ALLIEVE 5 (2013)", "JUNIOR 1 (2012)", "JUNIOR 2 (2011)",
  "SENIOR 1 (2010)", "SENIOR 2 (2009)", "SENIOR 3 (2008 e precedenti)",
];
export const RITMICA_LEVELS = ["START", "OPEN", "PREAGONISTICO", "AGONISTICO", "SERIE D", "COMBINATA", "AGONISTICO FGI"];
export const EXERCISE_TYPES = ["INDIVIDUALE", "COPPIA", "SQUADRA", "COMBINATA"];
export const APPARATUS = ["nastro", "cerchio", "fune", "clavette", "corpo libero"];

export const ARTISTICA_CATEGORIES = ["PULCINI/E", "GIOVANISSIMI/E", "ALLIEVI/E", "JUNIOR", "SENIOR"];
export const ARTISTICA_LEVELS = ["BASE", "PROMOZIONALE", "AVANZATO", "AGONISTICO", "SERIE C", "SERIE B", "SERIE A"];
export const ARTISTICA_APPARATUS = ["corpo libero", "volteggio", "trave", "parallele asimmetriche", "cavallo con maniglie", "anelli", "parallele pari", "sbarra"];

const emptyForm = (discipline, eventId = "") => ({
  first_name: "", last_name: "", birth_date: "", codice_fiscale: "", tessera_number: "",
  coach_name: "", coach_tessera: "", club: "", discipline, category: "", level: "", exercise_type: "",
  exercise_types: [], group_members: {}, group_extra_members: {}, music_by_type: {},
  apparatus: [], event_id: eventId, music_title: "", music_file_id: null, music_url: "", performance_order: 0,
  notes: "", decl_program: false, decl_no_other: false, decl_gdpr: false, privacy_accepted: false,
});

const GROUP_LABEL_TO_TYPE = { COPPIA: "coppia", SQUADRA: "squadra", COMBINATA: "combinata" };
const TYPE_LABEL = { coppia: "Coppia", squadra: "Squadra", combinata: "Combinata" };

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 pt-2">
    <Icon className="w-4 h-4 text-gold" />
    <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-gold">{children}</h4>
  </div>
);

export default function AthleteFormDialog({ open, onOpenChange, discipline, events, editing, onSaved, defaultEventId = "" }) {
  const [form, setForm] = useState(emptyForm(discipline, defaultEventId));
  const [saving, setSaving] = useState(false);
  const [roster, setRoster] = useState([]);
  const isRitmica = discipline === "ritmica";

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const types = editing.exercise_types?.length ? editing.exercise_types : (editing.exercise_type ? [editing.exercise_type] : []);
      let mbt = editing.music_by_type && Object.keys(editing.music_by_type).length ? editing.music_by_type : {};
      if (!mbt.INDIVIDUALE && (editing.music_title || editing.music_file_id || editing.music_url)) {
        mbt = { ...mbt, INDIVIDUALE: { music_title: editing.music_title || "", music_file_id: editing.music_file_id || null, music_url: editing.music_url || "" } };
      }
      setForm({ ...emptyForm(discipline), ...editing, exercise_types: types, group_members: editing.group_members || {}, group_extra_members: editing.group_extra_members || {}, music_by_type: mbt, apparatus: editing.apparatus || [], event_id: editing.event_id || "" });
    } else {
      setForm(emptyForm(discipline, defaultEventId));
    }
  }, [open, editing, discipline, defaultEventId]);

  useEffect(() => {
    if (!open) return;
    api.get("/athletes", { params: { discipline } }).then((r) => setRoster(r.data)).catch(() => setRoster([]));
  }, [open, discipline]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleApparatus = (a) => setForm((f) => ({
    ...f, apparatus: f.apparatus.includes(a) ? f.apparatus.filter((x) => x !== a) : [...f.apparatus, a],
  }));
  const toggleType = (t) => setForm((f) => ({
    ...f, exercise_types: f.exercise_types.includes(t) ? f.exercise_types.filter((x) => x !== t) : [...f.exercise_types, t],
  }));
  const toggleCompanion = (gt, id) => setForm((f) => {
    const cur = f.group_members[gt] || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    return { ...f, group_members: { ...f.group_members, [gt]: next } };
  });

  const companions = roster.filter((a) => a.id !== editing?.id);
  const autoName = (gt) => {
    const ids = form.group_members[gt] || [];
    const extras = (form.group_extra_members[gt] || []).map((n) => (n.includes(" ") ? n.split(" ").pop() : n));
    const names = [form.last_name || form.first_name, ...ids.map((id) => {
      const a = roster.find((x) => x.id === id);
      return a ? (a.last_name || a.first_name) : "";
    }), ...extras].filter(Boolean);
    return `${TYPE_LABEL[gt]} – ${names.join("/")}`;
  };

  const [uploadingLabel, setUploadingLabel] = useState("");
  const musicFor = (label) => ({ music_title: "", music_file_id: null, music_url: "", ...(form.music_by_type[label] || {}) });
  const setMusicField = (label, key, value) => setForm((f) => ({
    ...f, music_by_type: { ...f.music_by_type, [label]: { ...(f.music_by_type[label] || {}), [key]: value } },
  }));
  const uploadMusicFor = async (label, file) => {
    if (!file) return;
    setUploadingLabel(label);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/music/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => {
        const cur = f.music_by_type[label] || {};
        return { ...f, music_by_type: { ...f.music_by_type, [label]: { ...cur, music_file_id: data.file_id, music_title: cur.music_title || data.original_filename } } };
      });
      toast.success("Musica caricata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploadingLabel("");
    }
  };
  const addExtraCompanion = (gt, name) => {
    const n = (name || "").trim();
    if (!n) return;
    setForm((f) => ({ ...f, group_extra_members: { ...f.group_extra_members, [gt]: [...(f.group_extra_members[gt] || []), n] } }));
  };
  const removeExtraCompanion = (gt, idx) => setForm((f) => ({
    ...f, group_extra_members: { ...f.group_extra_members, [gt]: (f.group_extra_members[gt] || []).filter((_, i) => i !== idx) },
  }));
  const [extraInput, setExtraInput] = useState({});

  const save = async () => {
    if (!form.first_name || !form.last_name) { toast.error("Nome e cognome sono obbligatori"); return; }
    if (!form.category) { toast.error("Seleziona la categoria"); return; }
    if (!form.privacy_accepted) { toast.error("Devi accettare l'informativa sulla privacy"); return; }
    setSaving(true);
    try {
      const payload = { ...form, exercise_type: form.exercise_types.join(", "), event_id: form.event_id || null, performance_order: Number(form.performance_order) || 0 };
      if (editing) {
        await api.put(`/athletes/${editing.id}`, payload);
        toast.success("Iscrizione aggiornata");
      } else {
        await api.post("/athletes", payload);
        toast.success("Iscrizione inviata");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const title = `${editing ? "Modifica" : "Nuova"} iscrizione — Ginnastica ${isRitmica ? "Ritmica" : "Artistica"}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F172A] border-white/10 max-h-[90vh] overflow-y-auto max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
          <DialogDescription>Compila i dati dell'atleta e conferma l'iscrizione alla gara.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* DATI ANAGRAFICI */}
          <SectionTitle icon={User}>Dati anagrafici</SectionTitle>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Nome</Label><Input data-testid="athlete-firstname-input" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Cognome</Label><Input data-testid="athlete-lastname-input" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Data di nascita</Label><Input data-testid="athlete-birthdate-input" type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Codice fiscale</Label><Input data-testid="athlete-cf-input" value={form.codice_fiscale} onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())} className="mt-1.5" /></div>
            <div><Label>Numero tessera</Label><Input data-testid="athlete-tessera-input" value={form.tessera_number} onChange={(e) => set("tessera_number", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Società di appartenenza</Label><Input data-testid="athlete-club-input" value={form.club} onChange={(e) => set("club", e.target.value)} className="mt-1.5" /></div>
            <div><Label>Tecnico accompagnatore</Label><Input data-testid="athlete-coach-input" value={form.coach_name} onChange={(e) => set("coach_name", e.target.value)} className="mt-1.5" placeholder="Nome e cognome" /></div>
            <div><Label>Numero tessera accompagnatore</Label><Input data-testid="athlete-coach-tessera-input" value={form.coach_tessera} onChange={(e) => set("coach_tessera", e.target.value)} className="mt-1.5" /></div>
          </div>

          {/* INFORMAZIONI SPORTIVE */}
          <SectionTitle icon={Trophy}>Informazioni sportive</SectionTitle>
          {isRitmica ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="athlete-category-select" className="mt-1.5"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                    <SelectContent>{RITMICA_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Livello di partecipazione</Label>
                  <Select value={form.level} onValueChange={(v) => set("level", v)}>
                    <SelectTrigger data-testid="athlete-level-select" className="mt-1.5"><SelectValue placeholder="Seleziona livello" /></SelectTrigger>
                    <SelectContent>{RITMICA_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Attrezzi (seleziona uno o più)</Label>
                <div className="flex flex-wrap gap-3">
                  {APPARATUS.map((a) => (
                    <label key={a} data-testid={`apparatus-${a.replace(/\s+/g, "-")}`} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${form.apparatus.includes(a) ? "border-gold/60 bg-gold/10 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>
                      <Checkbox checked={form.apparatus.includes(a)} onCheckedChange={() => toggleApparatus(a)} />
                      <span className="text-sm capitalize">{a}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="athlete-category-select" className="mt-1.5"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                    <SelectContent>{ARTISTICA_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Livello di partecipazione</Label>
                  <Select value={form.level} onValueChange={(v) => set("level", v)}>
                    <SelectTrigger data-testid="athlete-level-select" className="mt-1.5"><SelectValue placeholder="Seleziona livello" /></SelectTrigger>
                    <SelectContent>{ARTISTICA_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Attrezzi / specialità (seleziona uno o più)</Label>
                <div className="flex flex-wrap gap-3">
                  {ARTISTICA_APPARATUS.map((a) => (
                    <label key={a} data-testid={`apparatus-${a.replace(/\s+/g, "-")}`} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${form.apparatus.includes(a) ? "border-gold/60 bg-gold/10 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>
                      <Checkbox checked={form.apparatus.includes(a)} onCheckedChange={() => toggleApparatus(a)} />
                      <span className="text-sm capitalize">{a}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* TIPO DI ESIBIZIONE (multi-selezione) */}
          <SectionTitle icon={Users}>Tipo di esibizione</SectionTitle>
          <p className="text-xs text-muted-foreground -mt-1">Puoi selezionare più tipi: l'atleta può gareggiare come Individuale e/o in gruppo (Coppia, Squadra, Combinata).</p>
          <div className="flex flex-wrap gap-3">
            {EXERCISE_TYPES.map((t) => (
              <label key={t} data-testid={`exercise-type-${t}`} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${form.exercise_types.includes(t) ? "border-gold/60 bg-gold/10 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>
                <Checkbox checked={form.exercise_types.includes(t)} onCheckedChange={() => toggleType(t)} />
                <span className="text-sm capitalize">{t}</span>
              </label>
            ))}
          </div>

          {Object.entries(GROUP_LABEL_TO_TYPE).map(([label, gt]) => (
            form.exercise_types.includes(label) && (
              <div key={gt} data-testid={`group-companions-${gt}`} className="rounded-lg border border-gold/25 bg-gold/5 p-4 space-y-2">
                <Label className="text-gold">Compagne per {TYPE_LABEL[gt]} ({(form.group_members[gt] || []).length} selezionate)</Label>
                <p className="text-xs text-muted-foreground">Seleziona le atlete già iscritte che formeranno il gruppo con {form.first_name || "questa atleta"}.</p>
                <div className="max-h-40 overflow-auto rounded-md border border-white/10 divide-y divide-white/5">
                  {companions.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-3">Nessun'altra atleta iscritta a questa disciplina. Puoi aggiungere le compagne dopo averle iscritte.</p>
                  ) : companions.map((a) => (
                    <label key={a.id} data-testid={`companion-${gt}-${a.id}`} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5">
                      <Checkbox checked={(form.group_members[gt] || []).includes(a.id)} onCheckedChange={() => toggleCompanion(gt, a.id)} />
                      <span className="text-sm">{a.first_name} {a.last_name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{a.category}{a.club ? ` · ${a.club}` : ""}</span>
                    </label>
                  ))}
                </div>
                <div className="pt-2 border-t border-white/10">
                  <Label className="text-xs text-muted-foreground">Aggiungi una compagna non ancora iscritta</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input
                      data-testid={`extra-companion-input-${gt}`}
                      value={extraInput[gt] || ""}
                      onChange={(e) => setExtraInput((s) => ({ ...s, [gt]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtraCompanion(gt, extraInput[gt]); setExtraInput((s) => ({ ...s, [gt]: "" })); } }}
                      placeholder="Nome e cognome"
                      className="h-9"
                    />
                    <Button type="button" data-testid={`extra-companion-add-${gt}`} variant="outline" className="border-white/15 h-9 shrink-0"
                      onClick={() => { addExtraCompanion(gt, extraInput[gt]); setExtraInput((s) => ({ ...s, [gt]: "" })); }}>
                      Aggiungi
                    </Button>
                  </div>
                  {(form.group_extra_members[gt] || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`extra-companions-list-${gt}`}>
                      {(form.group_extra_members[gt] || []).map((n, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white/5 border border-white/15">
                          {n}
                          <button type="button" data-testid={`extra-companion-remove-${gt}-${idx}`} onClick={() => removeExtraCompanion(gt, idx)} className="text-muted-foreground hover:text-destructive">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gold/80">Nome gruppo: <span className="font-medium">{autoName(gt)}</span></p>
              </div>
            )
          ))}

          {form.exercise_types.length > 0 && (
            <div data-testid="registration-summary" className="rounded-lg border border-white/10 bg-[#0F172A] p-4">
              <p className="text-sm font-semibold mb-2 flex items-center gap-2"><Trophy className="w-4 h-4 text-gold" /> Riepilogo iscrizione</p>
              <div className="flex flex-wrap gap-2">
                {form.exercise_types.map((t) => {
                  const gt = GROUP_LABEL_TO_TYPE[t];
                  const names = gt ? (form.group_members[gt] || []).map((id) => { const a = roster.find((x) => x.id === id); return a ? `${a.first_name} ${a.last_name}` : null; }).filter(Boolean) : [];
                  const extras = gt ? (form.group_extra_members[gt] || []) : [];
                  const all = [...names, ...extras];
                  return (
                    <span key={t} data-testid={`summary-type-${t}`} className="text-xs px-2.5 py-1 rounded-full bg-gold/15 text-gold border border-gold/25">
                      {t}{gt && all.length ? `: ${form.first_name || "atleta"} + ${all.join(", ")}` : ""}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* GARA */}
          <SectionTitle icon={Trophy}>Gara</SectionTitle>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Evento</Label>
              <Select value={form.event_id || "none"} onValueChange={(v) => set("event_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="athlete-event-select" className="mt-1.5"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {events.map((ev) => <SelectItem key={ev.id} value={ev.id}>{ev.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Ordine esibizione</Label><Input data-testid="athlete-order-input" type="number" value={form.performance_order} onChange={(e) => set("performance_order", e.target.value)} className="mt-1.5" /></div>
          </div>

          {/* MUSICA PER TIPO */}
          <SectionTitle icon={Music4}>Musica dell'esibizione</SectionTitle>
          {form.exercise_types.length === 0 ? (
            <p className="text-sm text-muted-foreground">Seleziona almeno un tipo di esibizione per aggiungere la musica.</p>
          ) : (
            <p className="text-xs text-muted-foreground -mt-1">Puoi caricare una musica diversa per ogni tipo di esibizione.</p>
          )}
          {form.exercise_types.map((label) => {
            const m = musicFor(label);
            const busy = uploadingLabel === label;
            return (
              <div key={label} data-testid={`music-block-${label}`} className="rounded-lg border border-white/10 bg-[#0F172A] p-4 space-y-3">
                <p className="text-sm font-semibold text-gold capitalize">{label.toLowerCase()}</p>
                <div><Label>Titolo musica</Label><Input data-testid={`music-title-${label}`} value={m.music_title} onChange={(e) => setMusicField(label, "music_title", e.target.value)} className="mt-1.5" placeholder="Titolo del brano" /></div>
                <label data-testid={`music-dropzone-${label}`} className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 hover:border-gold/50 rounded-lg py-4 cursor-pointer transition-colors">
                  {busy ? <Loader2 className="w-6 h-6 animate-spin text-gold" /> : m.music_file_id ? <CheckCircle2 className="w-6 h-6 text-[#10B981]" /> : <Upload className="w-6 h-6 text-gold" />}
                  <span className="text-sm text-muted-foreground">{busy ? "Caricamento…" : m.music_file_id ? (m.music_title || "File caricato") : "Carica file audio (mp3/wav)"}</span>
                  <input data-testid={`music-file-${label}`} type="file" className="hidden" onChange={(e) => uploadMusicFor(label, e.target.files[0])} />
                </label>
                <div>
                  <Label className="flex items-center gap-1.5"><LinkIcon className="w-3.5 h-3.5 text-gold" /> oppure link (YouTube, Spotify, Apple Music, MP3 diretto)</Label>
                  <Input data-testid={`music-url-${label}`} value={m.music_url} onChange={(e) => setMusicField(label, "music_url", e.target.value)} className="mt-1.5" placeholder="https://…" />
                </div>
              </div>
            );
          })}

          {/* DICHIARAZIONE */}
          <SectionTitle icon={FileCheck}>Dichiarazione</SectionTitle>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="decl-program" checked={form.decl_program} onCheckedChange={(v) => set("decl_program", !!v)} className="mt-0.5" />
              <span className="text-sm text-muted-foreground">Dichiaro di aver preso visione del programma tecnico OPES 2026</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="decl-no-other" checked={form.decl_no_other} onCheckedChange={(v) => set("decl_no_other", !!v)} className="mt-0.5" />
              <span className="text-sm text-muted-foreground">La Ginnasta NON ha partecipato ad altri programmi incompatibili (OPES e FGI)</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="decl-gdpr" checked={form.decl_gdpr} onCheckedChange={(v) => set("decl_gdpr", !!v)} className="mt-0.5" />
              <span className="text-sm text-muted-foreground">Autorizzo il trattamento dei dati personali (GDPR)</span>
            </label>
          </div>

          {/* NOTE */}
          <SectionTitle icon={StickyNote}>Note aggiuntive</SectionTitle>
          <Textarea data-testid="athlete-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Eventuali note…" />

          <label className="flex items-start gap-3 cursor-pointer pt-1">
            <Checkbox data-testid="privacy-accept" checked={form.privacy_accepted} onCheckedChange={(v) => set("privacy_accepted", !!v)} className="mt-0.5" />
            <span className="text-sm font-medium">Ho letto e accetto l'informativa sulla privacy</span>
          </label>
        </div>

        <DialogFooter>
          <Button data-testid="save-athlete-btn" onClick={save} disabled={saving} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Aggiorna iscrizione" : "Invia iscrizione"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
